import React, { useState, useEffect, useCallback } from "react";
import { FaGoogle, FaExclamationCircle } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import AnimatedModal from "./AnimatedModal";
import { authAPI } from "../utils/api";
import { API_BASE_URL } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

export default function LoginScreen({ isOpen, onClose, onSwitchToRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    console.log('🔐 Google OAuth Response received:', {
      hasCredential: !!response.credential,
      credentialLength: response.credential?.length,
      responseType: typeof response,
      origin: window.location.origin
    });
    
    setGoogleLoading(true);
    setError("");

    try {
      console.log('📡 Sending ID token to backend at:', `${API_BASE_URL}/api/auth/google`);
      // Send the ID token to your backend
      const result = await authAPI.loginWithGoogle(response.credential);
      
      console.log('✅ Backend response received:', result);
      // Use auth context to manage login
      login(result);
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error("❌ Google login failed:", error);
      console.error("Error details:", {
        status: error.status,
        message: error.message,
        data: error.data,
        stack: error.stack
      });
      
      if (error.data && error.data.message) {
        setError(error.data.message);
      } else if (error.message && error.message.includes('Failed to fetch')) {
        setError("Unable to connect to the server. Please check your internet connection.");
      } else {
        setError("Google login failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [login, onClose]);

  const initializeGoogleOAuth = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    // if (!clientId || clientId === 'your-google-client-id') {
    //   console.warn('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file');
    //   setError("Google OAuth is not configured. Please contact support.");
    //   return;
    // }

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      console.log('Google OAuth initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google OAuth:', error);
      setError("Failed to initialize Google OAuth. Please refresh the page.");
    }
  }, [handleGoogleCredentialResponse]);

  // Initialize Google OAuth
  useEffect(() => {
    // Check if Google OAuth is already loaded
    if (window.google && window.google.accounts) {
      initializeGoogleOAuth();
      return;
    }

    // Make the callback function globally available
    window.handleGoogleCredentialResponse = handleGoogleCredentialResponse;
    
    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && window.google.accounts) {
        initializeGoogleOAuth();
      }
    };

    script.onerror = () => {
      console.error('Failed to load Google OAuth script');
      setError("Failed to load Google OAuth. Please check your internet connection.");
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      // Cleanup global callback
      delete window.handleGoogleCredentialResponse;
    };
  }, [handleGoogleCredentialResponse, initializeGoogleOAuth]);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    console.log('Google Login Debug Info:', {
      clientId: clientId ? 'Set' : 'Not set',
      googleLoaded: !!(window.google && window.google.accounts),
      googleAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id),
      currentOrigin: window.location.origin,
      currentPort: window.location.port
    });
    
    if (!clientId || clientId === 'your-google-client-id') {
      setError("Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file");
      return;
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        console.log('🚀 Prompting Google OAuth...');
        window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Failed to prompt Google OAuth:', error);
        setError("Failed to open Google login. Please try again.");
      }
    } else {
      setError("Google OAuth not loaded. Please refresh the page and try again.");
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setError("");

    // Log the API domain being called
    console.log("Login API domain:", API_BASE_URL);

    try {
      const response = await authAPI.login({ email, password });
      
      // Use auth context to manage login with normalized response
      login(response);
      
      // Close the modal without showing alert
      onClose();
    } catch (error) {
      console.error("Login failed:", error);
      
      // Handle specific error cases
      if (error.status === 404 || (error.data && error.data.code === 'USER_NOT_FOUND')) {
        setError("Account not found. Please check your email or create a new account.");
      } else if (error.status === 401 || (error.data && error.data.code === 'INVALID_CREDENTIALS')) {
        setError("Invalid email or password. Please check your credentials and try again.");
      } else if (error.status === 500 || (error.data && error.data.code === 'SERVER_ERROR')) {
        setError("Server error. Please try again in a few moments.");
      } else if (error.data && error.data.message) {
        // Use server-provided error message if available
        setError(error.data.message);
      } else if (error.message && error.message.includes('Failed to fetch')) {
        setError("Unable to connect to the server. Please check your internet connection.");
      } else {
        // Fallback error message
        setError("Login failed. Please check your credentials and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-sm">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl font-bold w-6 h-6 flex items-center justify-center transition-colors duration-200"
        >
          ×
        </button>
        
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Login</h2>

        {/* Google login */}
        <button
          onClick={handleGoogleLogin}
          disabled={googleLoading}
          className={`w-full py-2 rounded-lg font-medium mb-4 transition flex items-center justify-center gap-2 ${
            googleLoading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-red-500 hover:bg-red-600"
          } text-white`}
        >
          <FaGoogle className="text-lg" />
          {googleLoading ? "Signing in..." : "Continue with Google"}
        </button>

        <div className="flex items-center mb-4">
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
          <span className="mx-3 text-sm text-gray-500 dark:text-gray-400">or</span>
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
        </div>

        {/* Email input */}
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(""); // Clear error when user types
            if (!passwordShown && e.target.value.includes("@")) {
              setPasswordShown(true);
            }
          }}
          placeholder="Enter your email"
          className="w-full mb-3 p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none"
        />

        {/* Password input (revealed after email is typed) */}
        {passwordShown && (
          <>
            <div className="relative mb-3">
              <input
                type={showPasswordText ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(""); // Clear error when user types
                }}
                placeholder="Password"
                className="w-full p-3 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPasswordText(v => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                aria-label={showPasswordText ? "Hide password" : "Show password"}
              >
                {showPasswordText ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm flex items-start gap-2">
                <FaExclamationCircle className="text-red-500 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </>
        )}

        {/* Forgot Password link - always visible */}
        <div className="mb-4 text-right">
          <button
            type="button"
            onClick={() => setShowForgotModal(true)}
            className="text-sm text-indigo-600 hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        <button
          onClick={handleLogin}
          disabled={isLoading}
          className={`w-full py-2 rounded-lg font-medium transition ${
            isLoading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-indigo-600 hover:bg-indigo-500"
          } text-white`}
        >
          {isLoading ? "Logging in..." : "Login"}
        </button>

        {/* Switch to register */}
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{" "}
            <button
              onClick={onSwitchToRegister}
              className="text-indigo-600 hover:text-indigo-500 font-medium transition-colors duration-200"
            >
              Register here
            </button>
          </span>
        </div>
      </div>
      {/* Forgot Password Modal */}
      <AnimatedModal isOpen={showForgotModal} onClose={() => { setShowForgotModal(false); setForgotMessage(""); setForgotError(""); setForgotEmail(""); }} className="w-full max-w-sm">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg relative">
          <button
            onClick={() => setShowForgotModal(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl font-bold w-6 h-6 flex items-center justify-center transition-colors duration-200"
          >
            ×
          </button>
          <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100">Reset Password</h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300 text-sm">Enter your email address and we'll send you a link to reset your password.</p>
          <input
            type="email"
            value={forgotEmail}
            onChange={e => { setForgotEmail(e.target.value); setForgotError(""); setForgotMessage(""); }}
            placeholder="Enter your email"
            className="w-full mb-3 p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none"
          />
          {forgotError && <div className="mb-3 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">{forgotError}</div>}
          {forgotMessage && <div className="mb-3 p-2 bg-green-100 border border-green-400 text-green-700 rounded text-sm">{forgotMessage}</div>}
          <button
            onClick={async () => {
              setForgotLoading(true);
              setForgotError("");
              setForgotMessage("");
              try {
                await authAPI.forgotPassword(forgotEmail);
                setForgotMessage("If an account with that email exists, a reset link has been sent.");
              } catch (err) {
                setForgotError(err.message || "Failed to send reset link. Try again.");
              } finally {
                setForgotLoading(false);
              }
            }}
            disabled={forgotLoading || !forgotEmail}
            className={`w-full py-2 rounded-lg font-medium transition ${forgotLoading ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500"} text-white`}
          >
            {forgotLoading ? "Sending..." : "Send Reset Link"}
          </button>
        </div>
      </AnimatedModal>
    </AnimatedModal>
  );
}
